import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../../../utility/axios';
import { message, Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import '../../../styles/settings/settings-common.css';
import '../../../styles/settings/profile.css';
import { useAuth } from '../../../hooks/useAuth';
import ChangePasswordModal from './ChangePasswordModal';

const Profile = ({ embedded }) => {
    const { updateUser } = useAuth();
    const { t, i18n } = useTranslation();
    const [form] = Form.useForm();
    const [profileData, setProfileData] = useState({
        unique_identifier: '',
        firstname: '',
        lastname: '',
        email: '',
        role: '',
        status: '',
        created_at: '',
        signup_type: ''
    });
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: ''
    });

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [fieldErrors, setFieldErrors] = useState({ firstname: false, lastname: false });
    const [shakeFields, setShakeFields] = useState({ firstname: false, lastname: false });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    
    // Get the initial tab from localStorage or default to 'profile'
    const getInitialTab = () => {
        const savedTab = localStorage.getItem('profileActiveTab');
        return savedTab && ['profile'].includes(savedTab) ? savedTab : 'profile';
    };
    
    const [activeTab, setActiveTab] = useState(getInitialTab);
    const navigate = useNavigate();
    const firstnameInputRef = useRef(null);
    const lastnameInputRef = useRef(null);

    const handleClearField = (field, ref) => {
        setFormData(prev => ({ ...prev, [field]: '' }));
        setFieldErrors(prev => ({ ...prev, [field]: false }));
        setTimeout(() => ref.current?.focus(), 0);
    };

    // Available tabs configuration
    const availableTabs = [
        { 
            id: 'profile', 
            label: t('profile.title'), 
            description: t('profile.description')
        },
    ];

    // Handle tab change
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        
        // Save to localStorage
        localStorage.setItem('profileActiveTab', tabId);
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setInitialLoading(true);
                const response = await axios.get('/api/profile/');
                setProfileData(response.data.profile);
                setFormData({
                    firstname: response.data.profile.firstname,
                    lastname: response.data.profile.lastname
                });
            } catch (error) {
                message.error(t('common.messages.error'));
            } finally {
                setInitialLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleSubmit = useCallback(async () => {
        const firstname = formData.firstname?.trim() || '';
        const lastname = formData.lastname?.trim() || '';
        if (!firstname || !lastname) {
            const errors = { firstname: !firstname, lastname: !lastname };
            setFieldErrors(errors);
            setShakeFields(errors);
            setTimeout(() => setShakeFields({ firstname: false, lastname: false }), 1000);
            return;
        }
        if (firstname === profileData.firstname && lastname === profileData.lastname) return;
        const activeEl = document.activeElement;
        setLoading(true);
        try {
            await axios.post('/api/profile/update/', {
                firstname,
                lastname
            });
            const updatedProfile = { ...profileData, firstname, lastname };
            setProfileData(updatedProfile);
            setFormData({ firstname, lastname });
            updateUser(updatedProfile);
            message.success(i18n.t('profile.profileUpdated'));
        } catch (error) {
            console.error('Error updating profile:', error.response?.data || error.message);
            message.error(i18n.t('common.messages.error'));
        } finally {
            setLoading(false);
            if (activeEl && typeof activeEl.focus === 'function' && document.body.contains(activeEl)) {
                requestAnimationFrame(() => requestAnimationFrame(() => activeEl.focus({ preventScroll: true })));
            }
        }
    }, [formData.firstname, formData.lastname, profileData, updateUser, i18n]);

    const hasInitializedRef = useRef(false);
    const skipSaveAfterLanguageChangeRef = useRef(false);
    useEffect(() => {
        const handler = () => {
            skipSaveAfterLanguageChangeRef.current = true;
        };
        i18n.on('languageChanged', handler);
        return () => { i18n.off('languageChanged', handler); };
    }, [i18n]);

    useEffect(() => {
        if (initialLoading) return;
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            return;
        }
        if (skipSaveAfterLanguageChangeRef.current) {
            skipSaveAfterLanguageChangeRef.current = false;
            return;
        }
        const timer = setTimeout(() => {
            const firstname = formData.firstname?.trim() || '';
            const lastname = formData.lastname?.trim() || '';
            if (firstname && lastname && (firstname !== profileData.firstname || lastname !== profileData.lastname)) {
                handleSubmit();
            }
        }, 2000);
        return () => clearTimeout(timer);
    }, [initialLoading, formData.firstname, formData.lastname, profileData.firstname, profileData.lastname, handleSubmit]);

    const handleChangePassword = () => {
        setShowChangePasswordModal(true);
    };

    const handleDeleteAccount = async () => {
        // For custom signup type, require password
        if (profileData.signup_type === 'custom') {
            if (!deletePassword) {
                message.error(t('profile.deleteModal.passwordRequired'));
                return;
            }
        }

        setDeleteLoading(true);
        try {
            const requestData = {};
            if (profileData.signup_type === 'custom') {
                requestData.password = deletePassword;
            }
            
            await axios.post('/api/profile/delete/', requestData);
            
            message.success(t('profile.deleteModal.success'));
            // Logout and redirect
            navigate('/auth/login');
        } catch (error) {
            console.error('Error deleting account:', error.response?.data || error.message);
            if (error.response?.data?.error) {
                message.error(error.response.data.error);
            } else {
                message.error(t('profile.deleteModal.error'));
            }
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDeleteModalClose = () => {
        setShowDeleteModal(false);
        setDeletePassword('');
    };

    if (initialLoading) {
        return (
            <div className="profile-container" data-settings-scroll>
            </div>
        );
    }

    return (
        <div className="profile-container" data-settings-scroll>
            <div className="profile-card">
                <div className="profile-avatar-section">
                    <div className="profile-avatar">
                        <span className="profile-avatar-initials">
                            {profileData.firstname?.charAt(0)}{profileData.lastname?.charAt(0)}
                        </span>
                    </div>
                    <div className="profile-name">
                        <h3>{profileData.firstname} {profileData.lastname}</h3>
                        <div className="profile-identifier">{profileData.unique_identifier}</div>
                    </div>
                </div>
                <div className="profile-content">
                    <div className="profile-section">
                        <Form form={form} onFinish={handleSubmit} layout="vertical" className="profile-form">
                            <div className="profile-card-layout">
                                <div className="profile-field-card">
                                    <div className="profile-field-content">
                                        <h3 className="profile-field-label">{t('profile.emailAddress')}</h3>
                                        <div className="profile-field-value">{profileData.email}</div>
                                    </div>
                                </div>
                                <div className="profile-field-card profile-field-card-editable">
                                    <div className="profile-field-content">
                                        <h3 className="profile-field-label">{t('profile.firstName')}</h3>
                                        <div className={`profile-field-input-wrapper${fieldErrors.firstname ? ' profile-field-input-wrapper-error' : ''}${shakeFields.firstname ? ' profile-field-input-wrapper-shake' : ''}`}>
                                            <input
                                                ref={firstnameInputRef}
                                                type="text"
                                                className="profile-field-input"
                                                value={formData.firstname}
                                                onChange={(e) => {
                                                    setFormData({ ...formData, firstname: e.target.value });
                                                    if (fieldErrors.firstname) setFieldErrors(prev => ({ ...prev, firstname: false }));
                                                }}
                                                placeholder={t('profile.firstName')}
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                className="profile-field-input-clear"
                                                onClick={() => handleClearField('firstname', firstnameInputRef)}
                                                disabled={loading}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="profile-field-card profile-field-card-editable">
                                    <div className="profile-field-content">
                                        <h3 className="profile-field-label">{t('profile.lastName')}</h3>
                                        <div className={`profile-field-input-wrapper${fieldErrors.lastname ? ' profile-field-input-wrapper-error' : ''}${shakeFields.lastname ? ' profile-field-input-wrapper-shake' : ''}`}>
                                            <input
                                                ref={lastnameInputRef}
                                                type="text"
                                                className="profile-field-input"
                                                value={formData.lastname}
                                                onChange={(e) => {
                                                    setFormData({ ...formData, lastname: e.target.value });
                                                    if (fieldErrors.lastname) setFieldErrors(prev => ({ ...prev, lastname: false }));
                                                }}
                                                placeholder={t('profile.lastName')}
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                className="profile-field-input-clear"
                                                onClick={() => handleClearField('lastname', lastnameInputRef)}
                                                disabled={loading}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="profile-field-card">
                                    <div className="profile-field-content">
                                        <h3 className="profile-field-label">{t('profile.role')}</h3>
                                        <div className="profile-field-value">{profileData.role}</div>
                                    </div>
                                </div>
                                <div className="profile-field-card">
                                    <div className="profile-field-content">
                                        <h3 className="profile-field-label">{t('profile.signupMethod')}</h3>
                                        <div className="profile-field-value">{profileData.signup_type}</div>
                                    </div>
                                </div>
                                <div className="profile-field-card">
                                    <div className="profile-field-content">
                                        <h3 className="profile-field-label">{t('profile.accountCreated')}</h3>
                                        <div className="profile-field-value">{profileData.created_at}</div>
                                    </div>
                                </div>
                                {profileData.signup_type !== 'google' && (
                                    <div className="profile-field-card profile-field-card-action">
                                        <div className="profile-field-content">
                                            <h3 className="profile-field-label">{t('profile.changePassword')}</h3>
                                            <button
                                                type="button"
                                                className="profile-password-btn"
                                                onClick={handleChangePassword}
                                            >
                                                {t('profile.changePassword')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <div className="profile-field-card profile-field-card-action">
                                    <div className="profile-field-content">
                                        <h3 className="profile-field-label">{t('profile.deleteAccount')}</h3>
                                        <button
                                            type="button"
                                            className="profile-delete-btn"
                                            onClick={() => setShowDeleteModal(true)}
                                        >
                                            {t('profile.deleteAccount')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
            
            {/* Delete Account Confirmation Modal */}
            {showDeleteModal && (
                <div
                    className="profile-modal-overlay"
                    onClick={(e) => {
                        if (e.target === e.currentTarget && !deleteLoading) {
                            handleDeleteModalClose();
                        }
                    }}
                >
                    <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="profile-modal-header">
                            <div className="profile-modal-icon warning">!</div>
                            <h3>{t('profile.deleteModal.title')}</h3>
                        </div>
                        
                        <div className="profile-modal-content">
                            <h4>{t('profile.deleteModal.confirmationTitle')}</h4>
                            <p>
                                {t('profile.deleteModal.warningMessage')}
                            </p>
                            
                            {profileData.signup_type === 'custom' && (
                                <>
                                    <p>{t('profile.deleteModal.passwordPrompt')}</p>
                                    <div className="profile-modal-form">
                                        <input
                                            type="password"
                                            placeholder={t('profile.deleteModal.passwordPlaceholder')}
                                            value={deletePassword}
                                            onChange={(e) => setDeletePassword(e.target.value)}
                                            className="profile-modal-input"
                                            autoFocus
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        
                        <div className="profile-modal-actions">
                            <button 
                                className="profile-modal-cancel"
                                onClick={handleDeleteModalClose}
                                disabled={deleteLoading}
                            >
                                {t('profile.deleteModal.cancel')}
                            </button>
                            <button 
                                className="profile-modal-delete"
                                onClick={handleDeleteAccount}
                                disabled={deleteLoading || (profileData.signup_type === 'custom' && !deletePassword)}
                            >
                                {deleteLoading ? t('profile.deleteModal.deleting') : t('profile.deleteModal.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Change Password Modal */}
            <ChangePasswordModal 
                visible={showChangePasswordModal}
                onClose={() => setShowChangePasswordModal(false)}
                profileData={profileData}
            />
        </div>
    );
};

export default Profile;